// Rain at a node from its own readings and the nearest official rain gauge
// (GET devices/<mac>/rain/?days=): the backend judges every reading that has
// an enclosure temperature against staged rules - by day the sun lost and the
// enclosure cooling make rain possible, rain at the gauge within the hour or
// our analysed level rising likely, at least 0.2 mm at the gauge within 15
// min or our level confirming confirmed; at night only our level counts - and
// groups the readings that meet them into events. Rain at the gauge the node
// showed no sign of is an event of its own (stage "gauge"). Without the gauge
// (none for the node, or stale) the humidity rising makes rain likely, as
// before. The node's temperature and humidity sensor sits inside its
// enclosure, so it lags behind the outside air: a rough estimate. Pure
// helpers: no React, network or storage.
import { duration } from "utils/fieldFormat";
import { finite, timeOf } from "utils/radarDiagnostics";
import { clockText, dayClockText } from "utils/radarRich";

// Lowest first, as the backend ranks them for the last hours.
export const RAIN_STAGES = ["possible", "gauge", "likely", "confirmed"];
export const RAIN_LABELS = {
  possible: "Possible",
  gauge: "Gauge only",
  likely: "Likely",
  confirmed: "Confirmed",
};
// A stage in a sentence: "Likely rain"; "Rain at the gauge only".
export const RAIN_NAMES = {
  possible: "Possible rain",
  gauge: "Rain at the gauge only",
  likely: "Likely rain",
  confirmed: "Confirmed rain",
};
// Each stage's colour: its chip and its marks on the timeline.
export const RAIN_COLORS = {
  possible: "#d4a017",
  gauge: "#8a63d2",
  likely: "#e07b2c",
  confirmed: "#2f6fd6",
};
// The gauge's own rain on the timeline, below the events.
export const GAUGE_COLOR = "#14808f";
export const GAUGE_STATES = ["ok", "stale", "unavailable"];
// The backend's rules by default (rain.RULES), for an answer without its
// `thresholds`.
export const RAIN_RULES = {
  day_start_h: 6,
  day_end_h: 19,
  window_min: 60,
  min_readings: 3,
  sun_min_a: 0.015,
  sun_lost_ratio: 0.3,
  cooling_c: 3,
  night_cooling_c: 2,
  humidity_window_min: 75,
  humidity_rise_pts: 5,
  level_rise_mm: 50,
  level_rising_mm: 30,
  level_before_min: 180,
  level_before_gap_min: 20,
  level_after_min: 120,
  event_gap_min: 60,
  current_hours: 6,
  gauge_window_min: 15,
  gauge_confirm_mm: 0.2,
  gauge_likely_min: 60,
  gauge_event_mm: 0.2,
  gauge_stale_min: 30,
};
export const RAIN_DAYS_MAX = 31;

// The page's window as the endpoint's days (1-31): 24 h is 1, 7 d is 7.
export const rainDays = (minutes) =>
  Math.max(
    1,
    Math.min(RAIN_DAYS_MAX, Math.round((finite(minutes) ?? 1440) / 1440) || 1),
  );

const stageOf = (v) => (RAIN_STAGES.includes(v) ? v : null);
const whole = (v) => {
  const n = finite(v);
  return n !== null && n >= 0 ? Math.round(n) : null;
};

// One event as the endpoint gives it (times as ms), else null.
function readEvent(e) {
  const tStart = timeOf(e?.t_start);
  const stage = stageOf(e?.stage);
  if (tStart === null || stage === null) return null;
  const tEnd = Math.max(tStart, timeOf(e.t_end) ?? tStart);
  const ev = e.evidence && typeof e.evidence === "object" ? e.evidence : {};
  return {
    tStart,
    tEnd,
    tStage: timeOf(e.t_stage) ?? tStart,
    stage,
    daytime: typeof e.daytime === "boolean" ? e.daytime : null,
    reasons: (Array.isArray(e.reasons) ? e.reasons : []).filter(
      (r) => typeof r === "string" && r.trim(),
    ),
    evidence: {
      sunDropPct: finite(ev.sun_drop_pct),
      coolingC: finite(ev.enclosure_cooling_c),
      humidityRisePts: finite(ev.humidity_rise_pts),
      waterRiseMm: finite(ev.water_rise_mm),
      levelBeforeMm: finite(ev.our_level_before_mm),
      levelAfterMm: finite(ev.our_level_after_mm),
      levelsBefore: whole(ev.our_levels_before),
      levelsAfter: whole(ev.our_levels_after),
      // The gauge's rain within 15 and 60 min of the event, and the most in
      // one 5-minute set (a gauge event's: its total); null when the event
      // was judged without the gauge.
      gaugeMm: finite(ev.gauge_mm),
      gaugeNearMm: finite(ev.gauge_near_mm),
      gaugePeakMm: finite(ev.gauge_peak_mm),
    },
    readings: whole(e.readings),
    // Judged with the gauge (else on the node's readings alone).
    withGauge: e.with_gauge === true,
    // Until a reading 2 h after its last one has come in, its stage can
    // still rise.
    settled: e.settled !== false,
  };
}

const text = (v) => (typeof v === "string" && v.trim() ? v : null);
const amountOf = (v) => {
  const n = finite(v);
  return n !== null && n >= 0 ? n : null;
};

// One station as the endpoint gives it: {station, stationId, distanceKm,
// lastHourMm, todayMm, asOf}.
function readStation(s) {
  return {
    station: text(s?.station),
    stationId: text(s?.station_id),
    distanceKm: amountOf(s?.distance_km),
    lastHourMm: amountOf(s?.last_hour_mm),
    todayMm: amountOf(s?.today_mm),
    asOf: timeOf(s?.as_of),
  };
}

// The gauge that judged the node's rain: its state (ok, stale or unavailable)
// and why, the nearest station and its rain, its 5-minute sets over the window
// ([{t, mm}], oldest first), the next two stations. null from a server
// without gauges.
export function readGauge(g) {
  if (!g || typeof g !== "object" || !GAUGE_STATES.includes(g.state))
    return null;
  return {
    state: g.state,
    detail: text(g.detail),
    ...readStation(g),
    series: (Array.isArray(g.series) ? g.series : [])
      .map((row) =>
        Array.isArray(row) ? { t: timeOf(row[0]), mm: amountOf(row[1]) } : null,
      )
      .filter((row) => row && row.t !== null && row.mm !== null)
      .sort((a, b) => a.t - b.t),
    nearby: (Array.isArray(g.nearby) ? g.nearby : [])
      .filter((s) => s && typeof s === "object")
      .map(readStation)
      .filter((s) => s.station || s.stationId),
    chosenAt: timeOf(g.chosen_at),
  };
}

// The endpoint's answer: the events, newest first; the current assessment
// (`readings` 0: the node has not reported, which is not dry); the window,
// the newest reading and analysis judged and how many; the rules in force.
// null without one.
export function readRain(data) {
  if (!data || typeof data !== "object") return null;
  const thresholds =
    data.thresholds && typeof data.thresholds === "object"
      ? data.thresholds
      : {};
  const rules = {
    ...RAIN_RULES,
    ...Object.fromEntries(
      Object.entries(thresholds).filter(([, v]) => finite(v) !== null),
    ),
  };
  const current =
    data.current && typeof data.current === "object" ? data.current : {};
  return {
    events: (Array.isArray(data.events) ? data.events : [])
      .map(readEvent)
      .filter(Boolean)
      .sort((a, b) => b.tStart - a.tStart),
    current: {
      stage: stageOf(current.stage),
      event: readEvent(current.event),
      hours: finite(current.hours) ?? rules.current_hours,
      readings: whole(current.readings),
    },
    days: finite(data.days),
    asOf: timeOf(data.as_of),
    radarAsOf: timeOf(data.radar_as_of),
    readings: whole(data.readings),
    analyses: whole(data.analyses),
    gauge: readGauge(data.gauge),
    rules,
  };
}

// "10 Oct, 11:56 am–12:16 pm SGT"; across midnight "10 Oct, 11:56 pm–11 Oct,
// 12:16 am SGT"; one reading "10 Oct, 11:56 am SGT".
const DAY = new Intl.DateTimeFormat("en-SG", {
  timeZone: "Asia/Singapore",
  day: "numeric",
  month: "short",
});
export function rainTimeText(event) {
  if (!event) return "";
  if (event.tEnd - event.tStart < 60000)
    return `${dayClockText(event.tStart)} SGT`;
  const sameDay =
    DAY.format(new Date(event.tStart)) === DAY.format(new Date(event.tEnd));
  return `${dayClockText(event.tStart)}–${sameDay ? clockText(event.tEnd) : dayClockText(event.tEnd)} SGT`;
}

const number = (v, digits = 1) =>
  v.toLocaleString(undefined, { maximumFractionDigits: digits });
const plural = (n, word) =>
  `${n.toLocaleString()} ${word}${n === 1 ? "" : "s"}`;
const analyses = (n) =>
  `${n.toLocaleString()} ${n === 1 ? "analysis" : "analyses"}`;

// An event's evidence as [label, words] pairs, those it has: "Sun", "down 93
// %"; "Our estimate", "up 299 mm: 1,172.9 → 873.7 mm from the radar (2
// analyses before, 2 after)"; "Rain gauge", "13.2 mm within 15 min, 13.2 mm
// within 60 min (at most 5.8 mm in 5 min)". `stage`: the event's (a gauge
// event's rain is its total); `rules`: the windows in force.
export function rainEvidence(ev, { stage = null, rules = RAIN_RULES } = {}) {
  if (!ev) return [];
  const window = finite(rules?.gauge_window_min) ?? RAIN_RULES.gauge_window_min;
  const near = Math.max(
    window,
    finite(rules?.gauge_likely_min) ?? RAIN_RULES.gauge_likely_min,
  );
  const peak =
    ev.gaugePeakMm !== null && ev.gaugePeakMm !== undefined
      ? ` (at most ${number(ev.gaugePeakMm)} mm in 5 min)`
      : "";
  const gauge =
    ev.gaugeMm === null || ev.gaugeMm === undefined
      ? null
      : stage === "gauge"
        ? `${number(ev.gaugeMm)} mm${peak}, no sign of it at the node`
        : `${number(ev.gaugeMm)} mm within ${number(window, 0)} min, ${number(ev.gaugeNearMm ?? ev.gaugeMm)} mm within ${number(near, 0)} min${peak}`;
  const level =
    ev.waterRiseMm !== null
      ? `${ev.waterRiseMm >= 0 ? "up" : "down"} ${number(Math.abs(ev.waterRiseMm), 0)} mm${
          ev.levelBeforeMm !== null && ev.levelAfterMm !== null
            ? `: ${number(ev.levelBeforeMm)} → ${number(ev.levelAfterMm)} mm from the radar`
            : ""
        }${
          ev.levelsBefore !== null && ev.levelsAfter !== null
            ? ` (${analyses(ev.levelsBefore)} before, ${ev.levelsAfter} after)`
            : ""
        }`
      : stage !== "gauge" && (ev.levelsBefore === 0 || ev.levelsAfter === 0)
        ? "no analyses on both sides to compare"
        : null;
  return [
    ev.sunDropPct !== null
      ? ["Sun", `down ${number(ev.sunDropPct, 0)} %`]
      : null,
    ev.coolingC !== null
      ? ["Enclosure", `cooled ${number(ev.coolingC)} °C`]
      : null,
    ev.humidityRisePts !== null
      ? ["Humidity", `up ${number(ev.humidityRisePts)} points`]
      : null,
    level !== null ? ["Our estimate", level] : null,
    gauge !== null ? ["Rain gauge", gauge] : null,
  ].filter(Boolean);
}

// The current assessment as the card's chip: [text, tone].
export function rainChip(rain) {
  const c = rain?.current;
  if (!c) return ["Not known yet", "is-off"];
  const last = `last ${number(c.hours, 1)} h`;
  if (c.readings === 0) return [`Not reporting · ${last}`, "is-off"];
  if (c.stage)
    return [`${RAIN_LABELS[c.stage]} · ${last}`, `is-rain-${c.stage}`];
  return [`None · ${last}`, "is-off"];
}

// The current assessment in a sentence.
export function rainNowText(rain) {
  const c = rain?.current;
  if (!c) return "";
  const hours = `${number(c.hours, 1)} h`;
  if (c.readings === 0)
    return `The node has not reported in the last ${hours}, so nothing can be said about rain: no readings is not dry.`;
  if (c.stage && c.event)
    return `${RAIN_NAMES[c.stage]} in the last ${hours}: ${rainTimeText(c.event)}${c.event.settled ? "" : ", still settling"}.`;
  if (c.stage) return `${RAIN_NAMES[c.stage]} in the last ${hours}.`;
  return `No rain seen in the last ${hours}${c.readings !== null ? ` (${plural(c.readings, "reading")} judged)` : ""}.`;
}

// How long an event stays unsettled after its last reading: until the
// humidity's, our level's and the gauge's windows after it have passed.
export const settleMinutes = (rules) =>
  Math.max(
    finite(rules?.humidity_window_min) ?? RAIN_RULES.humidity_window_min,
    finite(rules?.level_after_min) ?? RAIN_RULES.level_after_min,
    finite(rules?.gauge_likely_min) ?? RAIN_RULES.gauge_likely_min,
  );
export const settleText = (rules) => duration(settleMinutes(rules) * 60);

// The timeline's marks over [from, to] (ms): each event from its first to
// its last reading, at least a 300th of the window wide so that one reading
// shows. -> [{key, x1, x2, stage}]
export function rainMarks(events, from, to) {
  const least = Math.max(0, (to - from) / 300);
  return (events || [])
    .map((e) => {
      const middle = (e.tStart + e.tEnd) / 2;
      const half = Math.max((e.tEnd - e.tStart) / 2, least / 2);
      const x1 = Math.max(from, middle - half);
      const x2 = Math.min(to, middle + half);
      return x2 > x1
        ? { key: `rain-${e.tStart}`, x1, x2, stage: e.stage }
        : null;
    })
    .filter(Boolean);
}

// The gauge's rain on the timeline over [from, to] (ms): its rainy 5-minute
// sets, those back to back one mark, each from the start of its first set's
// 5 minutes to its last set's time, at least a 300th of the window wide.
// -> [{key, x1, x2, mm, peak}]
export function gaugeMarks(series, from, to) {
  const least = Math.max(0, (to - from) / 300);
  const runs = [];
  for (const { t, mm } of series || []) {
    if (!(mm > 0)) continue;
    const last = runs[runs.length - 1];
    if (last && t - last.end <= 5 * 60000) {
      last.end = t;
      last.mm += mm;
      last.peak = Math.max(last.peak, mm);
    } else runs.push({ start: t - 5 * 60000, end: t, mm, peak: mm });
  }
  return runs
    .map((r) => {
      const middle = (r.start + r.end) / 2;
      const half = Math.max((r.end - r.start) / 2, least / 2);
      const x1 = Math.max(from, middle - half);
      const x2 = Math.min(to, middle + half);
      return x2 > x1
        ? {
            key: `gauge-${r.end}`,
            x1,
            x2,
            mm: Math.round(r.mm * 10) / 10,
            peak: r.peak,
          }
        : null;
    })
    .filter(Boolean);
}

const km = (v) => `${number(v, v < 10 ? 1 : 0)} km`;
const mmText = (v) => (v === null ? "no readings" : `${number(v)} mm`);

// The station in a few words: "Test Gauge North, 0.4 km away".
export const stationText = (s) =>
  [
    s?.station ?? s?.stationId,
    s?.distanceKm !== null && s?.distanceKm !== undefined
      ? `${km(s.distanceKm)} away`
      : null,
  ]
    .filter(Boolean)
    .join(", ");

// The gauge's line on the card, after "Rain gauge ·": [text, tone]. ok: the
// station, its rain in the last hour and today; stale: that, and why the
// node's readings judge since; unavailable: why there is none.
export function gaugeText(gauge) {
  if (!gauge) return null;
  if (gauge.state === "unavailable")
    return [
      `none. ${gauge.detail ?? "None is known for this node."} Rain is judged on the node's readings alone.`,
      "is-off",
    ];
  const where = stationText(gauge) || "the nearest";
  if (gauge.state === "stale")
    return [
      `${where}: stale. ${gauge.detail ?? "Its newest reading is too old: rain since then is judged on the node's readings alone."}`,
      "is-stale",
    ];
  return [
    `${where}: ${mmText(gauge.lastHourMm)} in the last hour, ${mmText(gauge.todayMm)} today`,
    "is-ok",
  ];
}
